import React, { useEffect, forwardRef } from 'react';
import { withRef } from '@udecode/cn';
import { withHOC, getPluginOptions, useElement, setNodes, findNodePath } from '@udecode/plate-common';
import { ELEMENT_IMAGE, useMediaState } from '@udecode/plate-media';
import {
  ResizableProvider,
  Resizable,
  ResizeHandle,
} from '@udecode/plate-resizable';
import { HistoryEditor } from 'slate-history';
import { useCaptionString } from '@udecode/plate-caption';

import Progress from 'components/Progress';
import { MediaPopover } from '../MediaPopover';
import { useUpload } from '../upload/useUpload';
import { KEY_DOCUMENT_SETTINGS } from '../../document-settings/createDocumentSettingsPlugin';

import './image-element.scss';

export const ImageElement2 = withHOC(
  ResizableProvider,
  withRef(
    (props, ref) => {
      const {
        children,
        className,
        element,
        editor,
        attributes,
        style,
      } = props || {};
      const mediaState = useMediaState();
      const { align = 'center', unsafeUrl: elementUrl, readOnly } = mediaState || {};

      const { innerWidth } = getPluginOptions(editor, KEY_DOCUMENT_SETTINGS);

      // Gets the image status from store, if it is uploading.
      const upload = useUpload(element.filename);
      const { percent, status, url: uploadUrl } = upload;

      useEffect(() => {
        const applyUrlToImage = (path) => {
          setNodes(
            editor,
            { url: uploadUrl },
            {
              at: path,
            },
          );
        };
        // When image finishes uploading, url should be populated.
        // Set the url on the image node.
        if (status === 'success' && elementUrl !== uploadUrl) {
          const path = findNodePath(editor, element);
          // Need to ignore writing this operation to history because undo breaks if
          // this is recorded in the history.
          if (HistoryEditor.isHistoryEditor(editor)) {
            HistoryEditor.withoutSaving(editor, () => applyUrlToImage(path));
          }
          else {
            applyUrlToImage(path);
          }
        }
      }, [editor, element, status, elementUrl, uploadUrl]);

      return (
        <MediaPopover pluginKey={ELEMENT_IMAGE}>
          <div
            className={className}
            ref={ref}
          >
            <figure
              className="slate-ui-media"
              contentEditable={false}
              style={style}
              {...attributes}
            >
              <Resizable
                align={align}
                className="slate-ui-media__resize"
                options={{
                  align,
                  readOnly,
                  maxWidth: innerWidth,
                  minWidth: 100,
                }}
              >
                <ResizeHandle
                  className="slate-ui-media__resize-handle slate-ui-media__resize-handle--left"
                  options={{ direction: 'left' }}
                />
                {!elementUrl && percent && percent < 1 ? (
                  <UploadingImage
                    element={element}
                    progress={percent}
                    documentWidth={innerWidth}
                  />
                ) : (
                  <Image
                    className="slate-image"
                  />
                )}
                <ResizeHandle
                  className="slate-ui-media__resize-handle slate-ui-media__resize-handle--right"
                  options={{ direction: 'right' }}
                />
              </Resizable>
            </figure>

            {children}
          </div>
        </MediaPopover>
      );
    },
  ),
);

export const useImage = (props) => {
  const { url } = useElement();

  const captionString = useCaptionString();

  return {
    src: url,
    alt: captionString,
    draggable: true,
    ...props,
  };
};

export const Image = (props) => {
  const htmlProps = useImage(props);

  return (
    <img
      {...htmlProps}
    />
  );
};

// eslint-disable-next-line
const UploadingImage = forwardRef(({ element, progress, documentWidth }, ref) => {
  const { width, height } = element;
  const heightRatio = height / width;
  const maxHeight = documentWidth * heightRatio;
  const percentage = progress * 100;

  return (
    <div
      className="slate-image slate-image--uploading"
      style={{
        height,
        width,
        maxHeight,
        maxWidth: '100%',
      }}
    >
      <Progress
        value={percentage}
        className="slate-image__progress"
      />
    </div>
  );
});
